import { useCallback } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, useTheme } from '@mui/material';
import { prepareTrendChartData } from '@/helpers/analytics/charts.js';
import { analyticsStyles } from '@/themes/commonStyles/analytics.js';
import { styled } from '@mui/material/styles';
import DeviceDetailsStatsBox from './details/DeviceDetailsStatsBox.jsx';
import { useTranslate, useDataProvider, usePermissions } from 'react-admin';
import CollectedPackagesStatsContent from './details/CollectedPackagesStatsContent.jsx';
import TransactionsStatsContent from './details/TransactionsStatsContent.jsx';
import UptimeStatsContent from './details/UptimeStatsContent.jsx';
import DepositSumStatsContent from './details/DepositSumStatsContent.jsx';
import { periods } from '@/constants/Filters.jsx';
import { useQuery } from '@tanstack/react-query';
import DeviceDetailsTopTags from '@/components/device/details/DeviceDetailsTopTags.jsx';
import DeviceDetailsTopProducts from '@/components/device/details/DeviceDetailsTopProducts.jsx';
import { DateRangeInput } from '@/components/global/inputs/DateRangeInput.jsx';
import PropTypes from 'prop-types';
import { Palette } from '@/themes/themeOverrides/Palette';
import { startOfDay, subDays } from 'date-fns';
import { toIsoDateString } from '@/helpers/date/dateValue';

const getYesterdayIso = () => toIsoDateString(startOfDay(subDays(new Date(), 1)));
const calculateGrowth = (current, previous) => {
    if (previous === 0) {
        if (current === 0) return 0;
        return 100;
    }
    return ((current - previous) / previous) * 100;
};

const StyledPeriodForm = styled(Box)(() => ({
    ...analyticsStyles.periodInput,
    '& .MuiFormControl-root': {
        '& .MuiFormLabel-root.MuiInputLabel-shrink': {
            backgroundColor: Palette.white,
        },
    },
    '& .MuiInputBase-root.MuiInputBase-colorPrimary.MuiFilledInput-root.Mui-focused': {
        backgroundColor: Palette.white,
    },
    '& .MuiInputBase-root.MuiInputBase-colorPrimary.MuiFilledInput-root.Mui-focused:hover': {
        backgroundColor: Palette.white,
    },
    '& .MuiSelect-select.MuiInputBase-input.MuiFilledInput-input.MuiInputBase-inputSizeSmall:focus': {
        backgroundColor: Palette.white,
    },
    '& .MuiFormLabel-root.MuiInputLabel-root.MuiFormLabel-filled': {
        top: '-3px',
    },
}));

const DeviceDetailsStats = ({ analyticData, periodFilters, setPeriodFilters, elementId, isCollectionPoint = false, hideTitle = false }) => {
    const theme = useTheme();
    const dataProvider = useDataProvider();
    const translate = useTranslate();
    const permissions = usePermissions();
    const hasAccess = !permissions?.permissions?.isService;

    const filter = isCollectionPoint ? { collectionPointId: elementId, ...periodFilters } : { deviceId: elementId, ...periodFilters };

    const { data, isLoading, error } = useQuery({
        queryKey: ['productRanking', elementId, periodFilters, isCollectionPoint],
        queryFn: () => {
            return dataProvider.summary('analytic-products/product-ranking', { filter });
        },
        enabled: !!elementId && hasAccess,
        select: response => {
            return {
                products: response.data?.data?.slice(0, 5) || [],
                total: response.data?.total ?? response.total ?? 0,
            };
        },
    });

    const {
        data: topTags,
        isLoading: isTopTagsLoading,
        error: topTagsError,
    } = useQuery({
        queryKey: ['tagRanking', elementId, periodFilters, isCollectionPoint],
        queryFn: () => {
            return dataProvider.summary('analytic-products/tag-ranking', { filter });
        },
        enabled: !!elementId && hasAccess,
        select: response => {
            return {
                tags: response.data?.data || [],
            };
        },
    });

    const handleDateRangeChange = useCallback(
        ({ start, end }) => {
            setPeriodFilters({
                period: 'custom',
                periodStart_gte: start,
                periodEnd_lte: end,
            });
        },
        [setPeriodFilters],
    );

    const handlePeriodChange = useCallback(
        event => {
            const period = event.target.value;
            if (period === 'custom') {
                const yesterday = getYesterdayIso();
                setPeriodFilters({
                    period,
                    periodStart_gte: yesterday,
                    periodEnd_lte: yesterday,
                });
                return;
            }
            setPeriodFilters({ period });
        },
        [setPeriodFilters],
    );

    if (!analyticData || analyticData.length < 2 || !analyticData[0] || !analyticData[1]) {
        return null;
    }

    const transactionStats = analyticData[0].data;
    const analytic = analyticData[0].data;
    const previous = analyticData[1].data;
    const series = analyticData.find(s => s.id === 'series').data;
    const chartData = prepareTrendChartData(series, 'transactionsValidContainersCount');
    const growth = calculateGrowth(analytic.transactionsValidContainersCount, previous.transactionsValidContainersCount);

    const transactionsGrowth = calculateGrowth(analytic.transactionsCount, previous.transactionsCount);
    const avgTransactions = series.reduce((sum, entry) => sum + entry.transactionsCount, 0) / series.length;
    const avg = series.reduce((sum, entry) => sum + entry.transactionsValidContainersCount, 0) / series.length;
    const barColorThresholds = [0.75, 0.5];

    return (
        <>
            <Box className={`device-details-stats${hideTitle ? '' : ' padding-wrapper'}`}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        margin: hideTitle ? '0 0 20px' : '62px 0 20px',
                        [theme.breakpoints.down('md')]: {
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            margin: hideTitle ? '0 0 16px' : '40px 0 24px',
                            gap: '4px',
                        },
                    }}
                >
                    {!hideTitle && (
                        <Typography variant='h5'>
                            {isCollectionPoint
                                ? translate('resources.collectionPoint.labels.statisticsForCollectionPoint')
                                : translate('resources.collectionPoint.labels.deviceWork')}
                        </Typography>
                    )}
                    <StyledPeriodForm
                        className='period-form'
                        sx={{
                            ...(hideTitle && { width: '100%' }),
                            [theme.breakpoints.down('md')]: { width: '100%' },
                        }}
                    >
                        <FormControl className='period-select'>
                            <InputLabel>{translate('resources.collectionPoint.labels.period')}</InputLabel>
                            <Select value={periodFilters.period} label={translate('resources.collectionPoint.labels.period')} onChange={handlePeriodChange}>
                                {periods.map(period => (
                                    <MenuItem key={period.id} value={period.id}>
                                        {translate(period.name)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </StyledPeriodForm>
                </Box>
                {'custom' === periodFilters.period && (
                    <Box>
                        <FormControl>
                            <DateRangeInput
                                onChange={handleDateRangeChange}
                                start={periodFilters.periodStart_gte}
                                end={periodFilters.periodEnd_lte}
                                maxDate='yesterday'
                            />
                        </FormControl>
                    </Box>
                )}
                <Box
                    sx={{
                        display: 'flex',
                        gap: '20px',
                        justifyContent: 'space-between',
                        [theme.breakpoints.down('md')]: {
                            flexDirection: 'column',
                            gap: '4px',
                        },
                    }}
                >
                    <DeviceDetailsStatsBox title={translate('resources.device.labels.collectedPackagesCount')}>
                        <CollectedPackagesStatsContent
                            transactionStats={transactionStats}
                            growth={growth}
                            chartData={chartData}
                            avg={avg}
                            theme={theme}
                            barColorThresholds={barColorThresholds}
                        />
                    </DeviceDetailsStatsBox>
                    <DeviceDetailsStatsBox title={translate('common.transactionsCount')}>
                        <TransactionsStatsContent
                            transactionStats={transactionStats}
                            transactionsGrowth={transactionsGrowth}
                            chartData={chartData}
                            avgTransactions={avgTransactions}
                            theme={theme}
                            barColorThresholds={barColorThresholds}
                        />
                    </DeviceDetailsStatsBox>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        gap: '20px',
                        justifyContent: 'flex-start',
                        marginTop: '20px',
                        [theme.breakpoints.down('md')]: {
                            flexDirection: 'column',
                            gap: '4px',
                            marginTop: '4px',
                        },
                    }}
                >
                    <DeviceDetailsStatsBox title={translate('common.uptime')}>
                        <UptimeStatsContent transactionStats={transactionStats} theme={theme} />
                    </DeviceDetailsStatsBox>
                    <DeviceDetailsStatsBox title={translate('resources.collectionPoint.labels.depositSum')}>
                        <DepositSumStatsContent transactionStats={transactionStats} />
                    </DeviceDetailsStatsBox>
                </Box>
            </Box>

            {hasAccess && (
                <>
                    <DeviceDetailsTopProducts data={data} isLoading={isLoading} error={error} total={analytic.transactionsContainersCount || 0} />
                    <DeviceDetailsTopTags
                        topTags={topTags}
                        isLoading={isTopTagsLoading}
                        error={topTagsError}
                        total={analytic.transactionsContainersCount || 0}
                    />
                </>
            )}
        </>
    );
};

DeviceDetailsStats.propTypes = {
    analyticData: PropTypes.array,
    periodFilters: PropTypes.shape({
        period: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        periodStart_gte: PropTypes.string,
        periodEnd_lte: PropTypes.string,
    }).isRequired,
    setPeriodFilters: PropTypes.func.isRequired,
    elementId: PropTypes.string.isRequired,
    isCollectionPoint: PropTypes.bool,
    hideTitle: PropTypes.bool,
};

export default DeviceDetailsStats;
